/** Admin > Audit log: every sign-in, change and download, filterable and exportable. */
import { Download, SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Spinner } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { formatDate, navigate, timeAgo, toast } from "../lib/app";
import { URLS, useResource, type AuditEntry, type AuditFacets } from "./data";
import { Card, PageHead } from "./ui";

/** The filters as the URL and the API spell them; dates are YYYY-MM-DD in local time. */
type Filters = Record<"q" | "user" | "target" | "action" | "project" | "ip" | "outcome" | "from" | "to", string>;
const EMPTY: Filters = { q: "", user: "", target: "", action: "", project: "", ip: "", outcome: "", from: "", to: "" };
/** Filters kept behind "More filters" unless one of them is in use. */
const EXTRA: Array<keyof Filters> = ["project", "target", "ip"];

const GROUPS: Record<string, string> = {
  auth: "Signing in",
  account: "Accounts",
  admin: "Admin",
  project: "Projects",
  share: "Sharing",
  file: "Files",
  compile: "Compiling",
  sync: "Sync",
  history: "History",
  fun: "Just for fun",
};

function filtersFromUrl(): Filters {
  const p = new URLSearchParams(location.search);
  const f = { ...EMPTY };
  for (const k of Object.keys(f) as Array<keyof Filters>) f[k] = p.get(k) ?? "";
  return f;
}

/** Query string for the API: dates become a [from, to) range of local midnights, so "to" includes that whole day. */
function apiQuery(f: Filters, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (!v) continue;
    if (k === "from") p.set(k, String(new Date(`${v}T00:00`).getTime()));
    else if (k === "to") {
      const end = new Date(`${v}T00:00`);
      end.setDate(end.getDate() + 1);
      p.set(k, String(end.getTime()));
    } else p.set(k, v);
  }
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  return p.toString();
}

function urlQuery(f: Filters): string {
  const s = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  return s ? `?${s}` : "";
}

/** Plainer names for detail keys that aren't self-explanatory. */
const DETAIL_LABELS: Record<string, string> = { durationMs: "took", waitMs: "queued" };

function detailText(k: string, v: unknown): string {
  if (v === true) return "yes";
  if (v === false) return "no";
  // Timings are stored in milliseconds (keys ending in Ms).
  if (k.endsWith("Ms") && typeof v === "number") return v < 1000 ? `${v} ms` : `${(v / 1000).toFixed(1)} s`;
  return String(v);
}

/** A text box that only reports what was typed once typing pauses. */
function useDebounced(value: string, setValue: (v: string) => void): [string, (v: string) => void] {
  const [draft, setDraft] = useState(value);
  const timer = useRef<number>(undefined);
  useEffect(() => setDraft(value), [value]);
  return [
    draft,
    (v: string) => {
      setDraft(v);
      clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setValue(v), 300);
    },
  ];
}

/** A value in the table that filters the log by itself when clicked. */
function FilterLink({ onClick, title, children }: { onClick: () => void; title: string; children: ReactNode }) {
  return (
    <button type="button" className="audit-pick" title={title} onClick={onClick}>
      {children}
    </button>
  );
}

export function AuditLogPage() {
  const [filters, setFilters] = useState<Filters>(filtersFromUrl);
  const facets = useResource<AuditFacets>(URLS.facets, 60_000);
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [more, setMore] = useState(() => EXTRA.some((k) => filtersFromUrl()[k]));
  const request = useRef(0);

  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));
  const [q, setQ] = useDebounced(filters.q, (q) => set({ q }));
  const [ip, setIp] = useDebounced(filters.ip, (ip) => set({ ip }));

  useEffect(() => {
    // Keep the filters in the URL, so a view can be bookmarked or sent to another admin.
    navigate(`/admin/audit${urlQuery(filters)}`, true);
    const id = ++request.current;
    setEntries(null);
    api
      .get<{ entries: AuditEntry[]; next: number | null }>(`/api/admin/audit?${apiQuery(filters)}`)
      .then((r) => {
        if (id !== request.current) return;
        setEntries(r.entries);
        setNext(r.next);
      })
      .catch((e) => {
        if (id === request.current) setEntries([]);
        toast(errorMessage(e), "error");
      });
  }, [filters]);

  // Clicking a value in the table can set a filter that lives behind "More filters".
  useEffect(() => {
    if (EXTRA.some((k) => filters[k])) setMore(true);
  }, [filters.project, filters.target, filters.ip]);

  const actionGroups = useMemo(() => {
    const groups = new Map<string, AuditFacets["actions"]>();
    for (const a of facets.data?.actions ?? []) {
      const g = a.action.split(".")[0];
      groups.set(g, [...(groups.get(g) ?? []), a]);
    }
    return [...groups];
  }, [facets.data]);
  const labels = useMemo(() => new Map(facets.data?.actions.map((a) => [a.action, a.label])), [facets.data]);

  async function loadMore() {
    if (!next) return;
    setLoadingMore(true);
    const id = request.current;
    try {
      const r = await api.get<{ entries: AuditEntry[]; next: number | null }>(`/api/admin/audit?${apiQuery(filters, { before: String(next) })}`);
      if (id !== request.current) return;
      setEntries((e) => [...(e ?? []), ...r.entries]);
      setNext(r.next);
    } catch (e) {
      toast(errorMessage(e), "error");
    } finally {
      setLoadingMore(false);
    }
  }

  const active = Object.values(filters).filter(Boolean).length;
  const hiddenActive = EXTRA.filter((k) => filters[k]).length;
  const users = facets.data?.users ?? [];
  // Keep a filter from the URL selectable even when the menus don't list it (yet).
  const withCurrent = (list: string[], v: string) => (v && !list.some((x) => x.toLowerCase() === v.toLowerCase()) ? [v, ...list] : list);
  const projects = facets.data?.projects ?? [];
  const retention = facets.data?.retentionDays ?? 0;

  return (
    <>
      <PageHead
        title="Audit log"
        lede={
          <>
            Every sign-in, change and download, with who did it and where from. {retention > 0 ? `Entries are kept for ${retention} days. ` : ""}Click a name,
            action, project or address in the table to filter by it.
          </>
        }
        actions={
          <a className="btn" href={`/api/admin/audit.csv?${apiQuery(filters)}`} download>
            <Download size={14} /> Export CSV
          </a>
        }
      />

      <Card flush className="audit-card">
        <div className="audit-filters">
          <label className="audit-field audit-search">
            <span>Search</span>
            <input type="search" placeholder="Names, files, errors, addresses…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <label className="audit-field audit-action">
            <span>Action</span>
            <select value={filters.action} onChange={(e) => set({ action: e.target.value })}>
              <option value="">Any action</option>
              {actionGroups.map(([group, actions]) => (
                <optgroup key={group} label={GROUPS[group] ?? group}>
                  <option value={`${group}.*`}>All {(GROUPS[group] ?? group).toLowerCase()}</option>
                  {actions.map((a) => (
                    <option key={a.action} value={a.action}>
                      {a.label}
                    </option>
                  ))}
                </optgroup>
              ))}
              {filters.action && !labels.has(filters.action) && !filters.action.endsWith(".*") && <option value={filters.action}>{filters.action}</option>}
            </select>
          </label>
          <label className="audit-field">
            <span>Who</span>
            <select value={filters.user} onChange={(e) => set({ user: e.target.value })}>
              <option value="">Anyone</option>
              {withCurrent(users, filters.user).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
          <label className="audit-field">
            <span>Result</span>
            <select value={filters.outcome} onChange={(e) => set({ outcome: e.target.value })}>
              <option value="">Any</option>
              <option value="ok">Succeeded</option>
              <option value="failed">Failed or refused</option>
            </select>
          </label>
          <label className="audit-field">
            <span>From</span>
            <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => set({ from: e.target.value })} />
          </label>
          <label className="audit-field">
            <span>To</span>
            <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => set({ to: e.target.value })} />
          </label>
          {more && (
            <>
              <label className="audit-field">
                <span>Project</span>
                <select value={filters.project} onChange={(e) => set({ project: e.target.value })}>
                  <option value="">Any project</option>
                  {filters.project && !projects.some((p) => p.id === filters.project) && <option value={filters.project}>{filters.project}</option>}
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="audit-field">
                <span>Affected user</span>
                <select value={filters.target} onChange={(e) => set({ target: e.target.value })}>
                  <option value="">Anyone</option>
                  {withCurrent(users, filters.target).map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
              <label className="audit-field">
                <span>IP address</span>
                <input type="search" className="mono" placeholder="e.g. 203.0.113." value={ip} onChange={(e) => setIp(e.target.value.trim())} />
              </label>
            </>
          )}
        </div>
        <div className="audit-filter-bar">
          <button className="btn btn-sm btn-ghost" aria-expanded={more} onClick={() => setMore(!more)} disabled={more && hiddenActive > 0}>
            <SlidersHorizontal size={13} /> {more ? "Fewer filters" : "More filters"}
          </button>
          <span className="grow muted small">
            {entries === null ? "Loading…" : `${entries.length}${next ? "+" : ""} ${entries.length === 1 ? "entry" : "entries"}${active ? " match" : ""}`}
          </span>
          <button className="btn btn-sm btn-ghost" disabled={!active} onClick={() => setFilters(EMPTY)}>
            Clear filters{active ? ` (${active})` : ""}
          </button>
        </div>

        <div className="admin-scroll">
          <table className="table admin-table audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Project</th>
                <th>Details</th>
                <th>IP address</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {entries === null && (
                <tr>
                  <td colSpan={7}>
                    <Spinner />
                  </td>
                </tr>
              )}
              {entries?.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    {active ? "Nothing matches these filters." : "Nothing logged yet."}
                  </td>
                </tr>
              )}
              {entries?.map((e) => (
                <tr key={e.id} className={e.ok ? undefined : "audit-failed"}>
                  <td className="audit-when" title={new Date(e.at).toISOString()}>
                    <div>{formatDate(e.at)}</div>
                    <div className="muted small">{timeAgo(e.at)}</div>
                  </td>
                  <td>
                    {e.username ? (
                      <FilterLink onClick={() => set({ user: e.username })} title={`Only ${e.username}`}>
                        {e.username}
                      </FilterLink>
                    ) : (
                      <span className="muted">{e.ip ? "not signed in" : "server"}</span>
                    )}
                  </td>
                  <td>
                    <div>{labels.get(e.action) ?? e.action}</div>
                    <FilterLink onClick={() => set({ action: e.action })} title="Only this action">
                      <span className="mono muted">{e.action}</span>
                    </FilterLink>
                  </td>
                  <td>
                    {e.projectId ? (
                      <FilterLink onClick={() => set({ project: e.projectId ?? "" })} title="Only this project">
                        {e.projectName || e.projectId}
                      </FilterLink>
                    ) : (
                      <span className="muted">–</span>
                    )}
                  </td>
                  <td className="audit-details">
                    {e.targetUsername && (
                      <div>
                        <span className="muted">user </span>
                        <FilterLink onClick={() => set({ target: e.targetUsername })} title={`Only actions on ${e.targetUsername}`}>
                          {e.targetUsername}
                        </FilterLink>
                      </div>
                    )}
                    {Object.entries(e.details).map(([k, v]) => (
                      <div key={k}>
                        <span className="muted">{DETAIL_LABELS[k] ?? k} </span>
                        <span className="mono">{detailText(k, v)}</span>
                      </div>
                    ))}
                    {e.error && <div className="error-text">{e.error}</div>}
                    {!e.targetUsername && !Object.keys(e.details).length && !e.error && <span className="muted">–</span>}
                  </td>
                  <td>
                    {e.ip ? (
                      <FilterLink onClick={() => set({ ip: e.ip })} title={e.userAgent || "Only this address"}>
                        <span className="mono">{e.ip}</span>
                      </FilterLink>
                    ) : (
                      <span className="muted">–</span>
                    )}
                  </td>
                  <td>
                    {e.ok ? (
                      <span className="chip chip-ok">ok</span>
                    ) : (
                      <span className="chip chip-bad" title={`HTTP ${e.status}`}>
                        {e.status === 401 || e.status === 403 ? "refused" : "failed"} · {e.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {next && (
          <div className="audit-more">
            <button className="btn" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? <Spinner /> : null} Load older entries
            </button>
          </div>
        )}
      </Card>
    </>
  );
}
